import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  MapPin,
  Search,
  GraduationCap,
  Clock,
  BookOpen,
  CreditCard,
  FlaskConical,
  Library,
  Wallet,
  ArrowUpRight,
  ChevronRight,
  CheckCircle2,
  CalendarDays,
  Landmark,
  BadgeInfo,
  X,
  IndianRupee,
  SlidersHorizontal,
} from "lucide-react";

const DEFAULT_LOGO = "/assets/img/bubbanlogo.jpg";

const feesData = [
  {
    id: "1",
    program: "Bachelor of Science in Computer Science and Engineering",
    shortName: "Computer Science & Engineering",
    degree: "B.Sc. in CSE",
    duration: "4 Years",
    semester: "8",
    credit: "164 (GED/Allied:22.5, Core:109)",
    admission: "12000 (Once)",
    semester_fee: "5000 (Per Semester)",
    establihsment: "2000 (Per Semester)",
    library: "1000 (Once)",
    lab: "500 (Per Semester)",
    exam: "100 (Per Credit)",
    tuition: "2240 (Per Credit)",
    total: "4,42,760/=",
    category: "Engineering",
    icon: "engineering",
  },
  {
    id: "2",
    program: "Bachelor of Business Administration",
    shortName: "Business Administration",
    degree: "BBA",
    duration: "4 Years",
    semester: "8",
    credit: "141 (Foundation: 12, GED/Allied:30, Core:78)",
    admission: "12000 (Once)",
    semester_fee: "5000 (Per Semester)",
    establihsment: "2000 (Per Semester)",
    library: "1000 (Once)",
    lab: "500 (Per Semester)",
    exam: "100 (Per Credit)",
    tuition: "1910 (Per Credit)",
    total: "3,42,410/=",
    category: "Business",
    icon: "business",
  },
  {
    id: "3",
    program: "Bachelor of Hospitality and Tourism Management",
    shortName: "Hospitality & Tourism Management",
    degree: "BHTM",
    duration: "4 Years",
    semester: "8",
    credit: "141 (Foundation: 12, GED/Allied:30, Core:78)",
    admission: "12000 (Once)",
    semester_fee: "5000 (Per Semester)",
    establihsment: "2000 (Per Semester)",
    library: "1000 (Once)",
    lab: "500 (Per Semester)",
    exam: "100 (Per Credit)",
    tuition: "1910 (Per Credit)",
    total: "3,42,410/=",
    category: "Hospitality",
    icon: "hospitality",
  },
  {
    id: "4",
    program:"Bachelor of Social Science in Governance and Development Studies",
    shortName: "Governance & Development Studies",
    degree: "BSS in GDS",
    duration: "4 Years",
    semester: "8",
    credit: "142 (Foundation: 12, GED/Allied:34, Core:78)",
    admission: "10000 (Once)",
    semester_fee: "5000 (Per Semester)",
    establihsment: "2000 (Per Semester)",
    library: "1000 (Once)",
    lab: "500 (Per Semester)",
    exam: "100 (Per Credit)",
    tuition: "1660 (Per Credit)",
    total: "3,42,410/=",
    category: "Social Science",
    icon: "social",
  },
  {
    id: "5",
    program: "Bachelor of Arts in English",
    shortName: "English",
    degree: "B.A. in English",
    duration: "4 Years",
    semester: "8",
    credit: "155 (Foundation: 12, GED/Allied:36, Core:78)",
    admission: "12000 (Once)",
    semester_fee: "5000 (Per Semester)",
    establihsment: "2000 (Per Semester)",
    library: "1000 (Once)",
    lab: "500 (Per Semester)",
    exam: "100 (Per Credit)",
    tuition: "1521 (Per Credit)",
    total: "3,10,255/=",
    category: "Arts",
    icon: "arts",
  },
  {
    id: "6",
    program: "Master of Business Administration (Professional MBA)",
    shortName: "Professional MBA",
    degree: "Professional MBA",
    duration: "2 Years",
    semester: "4",
    credit: "60 (Foundation: 12, Concentration:12, Core:30)",
    admission: "12000 (Once)",
    semester_fee: "5000 (Per Semester)",
    establihsment: "2000 (Per Semester)",
    library: "1000 (Once)",
    lab: "0 (Per Semester)",
    exam: "100 (Per Credit)",
    tuition: "2300 (Per Credit)",
    total: "1,67,000/=",
    category: "Postgraduate",
    icon: "postgraduate",
  },
];

const programIcons = {
  engineering: GraduationCap,
  business: Landmark,
  hospitality: BookOpen,
  social: Library,
  arts: BookOpen,
  postgraduate: GraduationCap,
};

const formatNumber = (value) =>
  Number(value).toLocaleString("en-BD");

const getAmount = (value) => {
  const amount = String(value).replace(/,/g, "").match(/\d+/);
  return amount ? Number(amount[0]) : 0;
};

const parseTotal = (value) => {
  const amount = String(value).replace(/,/g, "").match(/\d+/);
  return amount ? Number(amount[0]) : 0;
};

const formatCurrency = (amount) =>
  `৳${Number(amount).toLocaleString("en-BD")}`;

export default function FeesPage({
  logoSrc = DEFAULT_LOGO,
  accent = "#115e32",
  pageTitle = "Fees | Bandarban University",
}) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Programs");
  const [viewMode, setViewMode] = useState("cards");
  const [selectedProgram, setSelectedProgram] = useState(null);

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const categories = [
    "All Programs",
    ...new Set(feesData.map((fee) => fee.category)),
  ];

  const filteredFees = useMemo(() => {
    const query = search.trim().toLowerCase();

    return feesData.filter((fee) => {
      const matchesSearch =
        !query ||
        fee.program.toLowerCase().includes(query) ||
        fee.shortName.toLowerCase().includes(query) ||
        fee.degree.toLowerCase().includes(query) ||
        fee.category.toLowerCase().includes(query);

      const matchesCategory =
        selectedCategory === "All Programs" ||
        fee.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [search, selectedCategory]);

  const stats = [
    {
      label: "Academic Programs",
      value: feesData.length,
      icon: GraduationCap,
      description: "Undergraduate & postgraduate",
    },
    {
      label: "Study Duration",
      value: "2–4 Years",
      icon: Clock,
      description: "Depending on your program",
    },
    {
      label: "Flexible Programs",
      value: "6 Options",
      icon: BookOpen,
      description: "Explore your future",
    },
  ];

  const resetFilters = () => {
    setSearch("");
    setSelectedCategory("All Programs");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.09 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div
      className="min-h-screen overflow-x-clip bg-slate-50 text-slate-800"
      style={{ "--fee-accent": accent }}
    >
      {/* ================= HERO SECTION ================= */}
      <section className="relative isolate overflow-hidden bg-gradient-to-br from-green-950 via-green-800 to-teal-700 text-white">
        {/* Decorative background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <motion.div
            animate={{ scale: [1, 1.15, 1], rotate: [0, 12, 0] }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-24 -top-28 h-96 w-96 rounded-full bg-emerald-400/15 blur-3xl"
          />

          <motion.div
            animate={{ scale: [1, 1.2, 1], x: [0, 25, 0] }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-36 left-1/4 h-96 w-96 rounded-full bg-teal-300/10 blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                "radial-gradient(circle, white 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
        </div>

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 sm:px-8 sm:py-20 lg:grid-cols-[1.15fr_0.85fr] lg:px-10 lg:py-24">
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-md">
              <GraduationCap size={17} />
              Bandarban University
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
              Academic Fee Structure
            </div>

            <h1 className="max-w-3xl text-4xl font-black leading-tight tracking-tight sm:text-5xl lg:text-6xl">
              Invest in Your
              <span className="mt-1 block text-emerald-300">
                Bright Future.
              </span>
            </h1>

            <p className="mt-6 max-w-2xl text-base leading-8 text-green-50/85 sm:text-lg">
              Explore our academic programs, understand the fee structure,
              and take the next step toward your educational journey at
              Bandarban University.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#program-fees"
                className="group inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3.5 font-bold text-green-900 shadow-lg transition duration-300 hover:-translate-y-1 hover:bg-emerald-50"
              >
                Explore Programs
                <ArrowUpRight
                  size={19}
                  className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </a>

              <a
                href="https://web.bubban.ac.bd/login"
                className="inline-flex items-center gap-2 rounded-xl border border-white/30 bg-white/10 px-6 py-3.5 font-semibold text-white backdrop-blur-sm transition hover:bg-white/20"
              >
                Admission Portal
                <ChevronRight size={18} />
              </a>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-green-50/80">
              <span className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-emerald-300" />
                Transparent fee details
              </span>
              <span className="flex items-center gap-2">
                <CheckCircle2 size={17} className="text-emerald-300" />
                Multiple academic programs
              </span>
            </div>
          </motion.div>

          {/* Hero visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative mx-auto w-full max-w-lg"
          >
            <div className="absolute -inset-4 rounded-[2rem] bg-emerald-300/10 blur-xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-white/20 bg-white/10 p-5 shadow-2xl backdrop-blur-xl sm:p-7">
              <div className="flex items-center justify-between gap-4 border-b border-white/15 pb-5">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-white p-1.5">
                    <img
                      src={logoSrc}
                      alt="Bandarban University"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div>
                    <p className="font-bold">Bandarban University</p>
                    <p className="mt-1 text-xs text-green-50/70">
                      Academic Fee Overview
                    </p>
                  </div>
                </div>
                <BadgeInfo size={22} className="text-emerald-300" />
              </div>

              <div className="py-6">
                <p className="text-sm text-green-50/75">
                  Find the right program for you
                </p>

                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                    <GraduationCap
                      size={23}
                      className="mb-3 text-emerald-300"
                    />
                    <p className="text-2xl font-black">
                      {feesData.length}
                    </p>
                    <p className="mt-1 text-xs text-green-50/75">
                      Listed programs
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                    <CalendarDays
                      size={23}
                      className="mb-3 text-emerald-300"
                    />
                    <p className="text-2xl font-black">2–4</p>
                    <p className="mt-1 text-xs text-green-50/75">
                      Years of study
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-3 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-4">
                  <Wallet className="shrink-0 text-emerald-300" size={22} />
                  <div>
                    <p className="text-sm font-bold">Plan your education</p>
                    <p className="mt-1 text-xs leading-5 text-green-50/75">
                      Compare the published fees for your preferred program.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <motion.div
              animate={{ y: [0, -9, 0] }}
              transition={{ duration: 3.5, repeat: Infinity }}
              className="absolute -left-4 top-16 hidden rounded-xl border border-white/20 bg-white p-3 text-green-900 shadow-xl sm:block"
            >
              <GraduationCap size={26} />
            </motion.div>

            <motion.div
              animate={{ y: [0, 9, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="absolute -right-3 bottom-16 hidden rounded-xl border border-white/20 bg-white p-3 text-green-900 shadow-xl sm:block"
            >
              <BookOpen size={25} />
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-emerald-300 via-teal-300 to-green-400" />
      </section>

      {/* ================= STATISTICS ================= */}
      <section className="relative z-10 mx-auto -mt-1 max-w-7xl px-5 pt-8 sm:px-8 lg:px-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="grid gap-4 sm:grid-cols-3"
        >
          {stats.map((stat) => {
            const Icon = stat.icon;

            return (
              <motion.div
                variants={itemVariants}
                key={stat.label}
                whileHover={{ y: -5 }}
                className="group rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm transition-shadow hover:shadow-xl sm:p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-800 transition-colors group-hover:bg-green-800 group-hover:text-white">
                    <Icon size={23} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                    <p className="mt-1 text-2xl font-black text-slate-900">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {stat.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* ================= PROGRAM FEES ================= */}
      <section
        id="program-fees"
        className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end"
        >
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-green-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-green-800">
              <CreditCard size={15} />
              Tuition & Other Charges
            </div>

            <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
              Explore Program Fees
            </h2>

            <p className="mt-3 max-w-2xl leading-7 text-slate-500">
              Find your preferred academic program and review its duration,
              credit requirements, and published fee breakdown.
            </p>
          </div>

          <div className="inline-flex w-fit items-center gap-2 rounded-xl border border-green-100 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
            <IndianRupee size={18} className="text-green-800" />
            Currency: <strong className="text-slate-900">BDT (৳)</strong>
          </div>
        </motion.div>

        {/* Search and filters */}
        <div className="mb-8 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search program, degree or subject..."
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3.5 pl-12 pr-10 text-sm outline-none transition focus:border-green-600 focus:bg-white focus:ring-4 focus:ring-green-100"
                aria-label="Search academic programs"
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                >
                  <X size={16} />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 flex items-center gap-2 text-sm font-semibold text-slate-500">
                <SlidersHorizontal size={17} />
                Filter:
              </span>

              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="min-w-0 max-w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-medium outline-none focus:border-green-600 focus:ring-4 focus:ring-green-100"
                aria-label="Filter by academic category"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>

              <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                <button
                  type="button"
                  onClick={() => setViewMode("cards")}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    viewMode === "cards"
                      ? "bg-green-800 text-white shadow-sm"
                      : "text-slate-600 hover:bg-white"
                  }`}
                >
                  Cards
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    viewMode === "table"
                      ? "bg-green-800 text-white shadow-sm"
                      : "text-slate-600 hover:bg-white"
                  }`}
                >
                  Table
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
            <p className="text-sm text-slate-500">
              Showing{" "}
              <strong className="text-slate-900">
                {filteredFees.length}
              </strong>{" "}
              of {feesData.length} academic programs
            </p>

            {(search || selectedCategory !== "All Programs") && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-sm font-semibold text-green-800 hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Program cards */}
        <AnimatePresence mode="wait">
          {viewMode === "cards" ? (
            <motion.div
              key="program-cards"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, y: 10 }}
              className="grid items-stretch gap-5 sm:grid-cols-2 xl:grid-cols-3"
            >
              {filteredFees.map((fee, index) => {
                const Icon = programIcons[fee.icon] || GraduationCap;

                return (
                  <motion.article
                    variants={itemVariants}
                    layout
                    key={fee.id}
                    whileHover={{ y: -6 }}
                    transition={{ duration: 0.25 }}
                    className="group relative flex min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:border-green-200 hover:shadow-xl"
                  >
                    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-green-800 via-emerald-500 to-teal-400" />

                    <div className="p-5 sm:p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-50 text-green-800 transition-all duration-300 group-hover:bg-green-800 group-hover:text-white group-hover:shadow-lg">
                          <Icon size={25} />
                        </div>

                        <span className="rounded-full border border-green-100 bg-green-50 px-3 py-1.5 text-xs font-bold text-green-800">
                          {fee.category}
                        </span>
                      </div>

                      <p className="mt-5 text-xs font-bold uppercase tracking-wider text-slate-400">
                        {fee.degree}
                      </p>

                      <h3 className="mt-2 min-h-[3.5rem] text-lg font-extrabold leading-7 text-slate-900 transition-colors group-hover:text-green-800">
                        {fee.shortName}
                      </h3>

                      <p className="mt-2 min-h-[2.5rem] text-sm leading-6 text-slate-500">
                        {fee.program}
                      </p>

                      <div className="mt-5 grid grid-cols-2 gap-3">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <Clock
                            size={17}
                            className="mb-2 text-green-800"
                          />
                          <p className="text-xs text-slate-500">Duration</p>
                          <p className="mt-1 font-bold text-slate-800">
                            {fee.duration}
                          </p>
                        </div>

                        <div className="rounded-xl bg-slate-50 p-3">
                          <CalendarDays
                            size={17}
                            className="mb-2 text-green-800"
                          />
                          <p className="text-xs text-slate-500">Semesters</p>
                          <p className="mt-1 font-bold text-slate-800">
                            {fee.semester}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-start gap-2 rounded-xl border border-slate-100 p-3">
                        <BookOpen
                          size={18}
                          className="mt-0.5 shrink-0 text-green-800"
                        />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-500">
                            Total Credits
                          </p>
                          <p className="mt-1 break-words text-sm font-semibold leading-5 text-slate-800">
                            {fee.credit}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-auto border-t border-slate-100 bg-gradient-to-b from-white to-green-50/60 p-5 sm:p-6">
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Total Payable
                      </p>

                      <div className="mt-1 flex flex-wrap items-baseline gap-1">
                        <span className="text-3xl font-black tracking-tight text-green-900">
                          ৳{parseTotal(fee.total).toLocaleString("en-BD")}
                        </span>
                        <span className="text-sm font-semibold text-slate-400">
                          BDT
                        </span>
                      </div>

                      <p className="mt-2 text-xs leading-5 text-slate-500">
                        Published program total. Please confirm the current
                        fee schedule with the university.
                      </p>

                      <button
                        type="button"
                        onClick={() => setSelectedProgram(fee)}
                        className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-green-800 px-4 py-3 font-bold text-white transition-all duration-300 hover:bg-green-900 hover:shadow-lg"
                      >
                        View Fee Breakdown
                        <ArrowUpRight
                          size={18}
                          className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                        />
                      </button>
                    </div>
                  </motion.article>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              key="program-table"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="min-w-0"
            >
              {/* Only this table container scrolls horizontally */}
              <div className="w-full min-w-0 overflow-x-auto overscroll-x-contain rounded-2xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full min-w-[1550px] border-collapse text-sm">
                  <thead>
                    <tr className="bg-green-900 text-white">
                      {[
                        "Program Name",
                        "Duration",
                        "Semesters",
                        "Total Credits",
                        "Admission Fee",
                        "Semester Fee",
                        "Establishment Fee",
                        "Library Fee",
                        "Lab Fee",
                        "Exam Fee",
                        "Tuition Fee",
                        "Total Payable",
                        "Details",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className="whitespace-nowrap border-r border-white/10 px-5 py-4 text-left font-semibold last:border-r-0"
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredFees.map((fee, index) => (
                      <tr
                        key={fee.id}
                        className={`border-b border-slate-100 transition-colors hover:bg-green-50 ${
                          index % 2 ? "bg-slate-50/60" : "bg-white"
                        }`}
                      >
                        <td className="max-w-[320px] whitespace-normal px-5 py-4 font-semibold text-slate-800">
                          {fee.program}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.duration}
                        </td>
                        <td className="px-5 py-4 text-center">
                          {fee.semester}
                        </td>
                        <td className="max-w-[240px] whitespace-normal px-5 py-4">
                          {fee.credit}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.admission}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.semester_fee}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.establihsment}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.library}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.lab}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.exam}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4">
                          {fee.tuition}
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 font-extrabold text-green-900">
                          {fee.total}
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => setSelectedProgram(fee)}
                            className="whitespace-nowrap rounded-lg bg-green-100 px-3 py-2 font-semibold text-green-900 transition hover:bg-green-800 hover:text-white"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}

                    {filteredFees.length === 0 && (
                      <tr>
                        <td
                          colSpan={13}
                          className="px-5 py-14 text-center text-slate-500"
                        >
                          No programs match your search. Try another keyword
                          or clear your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <p className="mt-3 text-xs text-slate-500">
                On smaller screens, swipe inside the table to see all fee
                columns.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {viewMode === "cards" && filteredFees.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-14 text-center"
          >
            <Search size={36} className="mx-auto text-slate-300" />
            <h3 className="mt-4 text-lg font-bold text-slate-800">
              No programs found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              Try a different search term or reset your filters.
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 rounded-xl bg-green-800 px-5 py-3 font-semibold text-white transition hover:bg-green-900"
            >
              Show All Programs
            </button>
          </motion.div>
        )}
      </section>

      {/* ================= FEE BREAKDOWN MODAL ================= */}
      <AnimatePresence>
        {selectedProgram && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) {
                setSelectedProgram(null);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSelectedProgram(null);
            }}
          >
            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="fee-modal-title"
              initial={{ opacity: 0, y: 25, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 15, scale: 0.97 }}
              transition={{ duration: 0.25 }}
              className="my-auto max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            >
              <div className="relative overflow-hidden bg-gradient-to-br from-green-950 via-green-800 to-teal-700 p-6 text-white sm:p-8">
                <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-emerald-300/10 blur-2xl" />

                <div className="relative flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold">
                      {selectedProgram.category}
                    </span>
                    <h2
                      id="fee-modal-title"
                      className="mt-4 text-2xl font-black leading-tight sm:text-3xl"
                    >
                      {selectedProgram.shortName}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-green-50/80">
                      {selectedProgram.program}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setSelectedProgram(null)}
                    aria-label="Close fee breakdown"
                    className="shrink-0 rounded-xl border border-white/20 bg-white/10 p-2 transition hover:bg-white/20"
                  >
                    <X size={21} />
                  </button>
                </div>

                <div className="relative mt-6 rounded-2xl border border-white/15 bg-white/10 p-5">
                  <p className="text-sm text-green-50/80">
                    Total Payable
                  </p>
                  <p className="mt-1 text-4xl font-black">
                    ৳{parseTotal(selectedProgram.total).toLocaleString("en-BD")}
                  </p>
                  <p className="mt-2 text-xs text-green-50/75">
                    As listed in the supplied fee schedule.
                  </p>
                </div>
              </div>

              <div className="p-5 sm:p-8">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-slate-50 p-4">
                    <Clock size={19} className="mb-2 text-green-800" />
                    <p className="text-xs text-slate-500">Duration</p>
                    <p className="mt-1 font-bold text-slate-900">
                      {selectedProgram.duration}
                    </p>
                  </div>
                  <div className="rounded-xl bg-slate-50 p-4">
                    <CalendarDays
                      size={19}
                      className="mb-2 text-green-800"
                    />
                    <p className="text-xs text-slate-500">Semesters</p>
                    <p className="mt-1 font-bold text-slate-900">
                      {selectedProgram.semester}
                    </p>
                  </div>
                </div>

                <h3 className="mb-4 mt-7 flex items-center gap-2 text-lg font-extrabold text-slate-900">
                  <CreditCard size={21} className="text-green-800" />
                  Fee Structure
                </h3>

                <div className="overflow-hidden rounded-xl border border-slate-200">
                  {[
                    ["Admission Fee", selectedProgram.admission],
                    ["Semester Fee", selectedProgram.semester_fee],
                    ["Establishment Fee", selectedProgram.establihsment],
                    ["Library Fee", selectedProgram.library],
                    ["Lab Fee", selectedProgram.lab],
                    ["Exam Fee", selectedProgram.exam],
                    ["Tuition Fee", selectedProgram.tuition],
                  ].map(([label, value], index) => (
                    <div
                      key={label}
                      className={`flex items-start justify-between gap-4 px-4 py-3.5 text-sm ${
                        index % 2 === 0 ? "bg-slate-50" : "bg-white"
                      }`}
                    >
                      <span className="text-slate-600">{label}</span>
                      <span className="text-right font-semibold text-slate-900">
                        {value}
                      </span>
                    </div>
                  ))}

                  <div className="flex items-center justify-between gap-4 border-t border-green-200 bg-green-50 px-4 py-4">
                    <span className="font-extrabold text-green-900">
                      Total Payable
                    </span>
                    <span className="text-lg font-black text-green-900">
                      {selectedProgram.total}
                    </span>
                  </div>
                </div>

                <div className="mt-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <BadgeInfo
                    size={20}
                    className="mt-0.5 shrink-0 text-amber-700"
                  />
                  <p className="text-xs leading-5 text-amber-900">
                    These figures reproduce the supplied fee data. Please
                    contact the university for confirmation of current
                    charges, payment schedules, and applicable conditions.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedProgram(null)}
                  className="mt-6 w-full rounded-xl bg-green-800 px-5 py-3.5 font-bold text-white transition hover:bg-green-900"
                >
                  Close Details
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ================= FEE INFORMATION ================= */}
      <section className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="overflow-hidden rounded-2xl border border-green-100 bg-gradient-to-br from-green-50 via-white to-teal-50 p-6 sm:p-8 lg:p-10"
        >
          <div className="grid items-start gap-6 md:grid-cols-[1fr_auto] md:items-center">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-green-800 text-white shadow-lg">
                <BadgeInfo size={25} />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 sm:text-2xl">
                  Need Help Understanding the Fees?
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                  Contact the university for clarification about tuition,
                  admission charges, semester payments, and other academic
                  fees before making your admission decision.
                </p>
              </div>
            </div>

            <a
              href="mailto:admission@bubban.ac.bd"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-800 px-5 py-3.5 font-bold text-white shadow-md transition hover:-translate-y-1 hover:bg-green-900 hover:shadow-lg"
            >
              Contact Admissions
              <ArrowUpRight size={18} />
            </a>
          </div>
        </motion.div>
      </section>

      {/* ================= CONTACT SECTION ================= */}
      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-10">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto mb-10 max-w-2xl text-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-4 py-2 text-xs font-bold uppercase tracking-wider text-green-800">
              <Landmark size={15} />
              Get in Touch
            </span>
            <h2 className="mt-4 text-3xl font-black text-slate-900 sm:text-4xl">
              We Are Here to Help
            </h2>
            <p className="mt-3 leading-7 text-slate-500">
              Reach out to our admissions team for more information about
              academic programs and fees.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            className="grid gap-5 md:grid-cols-3"
          >
            {[
              {
                title: "Email Us",
                detail: "admission@bubban.ac.bd",
                href: "mailto:admission@bubban.ac.bd",
                icon: Mail,
                caption: "Send us your questions",
              },
              {
                title: "Call Admissions",
                detail: "+880-131-8305900",
                href: "tel:+8801318305900",
                icon: Phone,
                caption: "Speak with our team",
              },
              {
                title: "Visit Our Campus",
                detail:
                  "Admission Office, Rowadaw Heights Main Road, Bandarban",
                href: null,
                icon: MapPin,
                caption: "Bandarban, Bangladesh",
              },
            ].map((contact) => {
              const Icon = contact.icon;

              return (
                <motion.div
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  key={contact.title}
                  className="group rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm transition-all duration-300 hover:border-green-200 hover:shadow-xl sm:p-8"
                >
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-green-800 transition-colors duration-300 group-hover:bg-green-800 group-hover:text-white">
                    <Icon size={26} />
                  </div>

                  <h3 className="mt-5 text-lg font-extrabold text-slate-900">
                    {contact.title}
                  </h3>

                  <p className="mt-2 text-xs text-slate-500">
                    {contact.caption}
                  </p>

                  {contact.href ? (
                    <a
                      href={contact.href}
                      className="mt-3 inline-block break-words text-sm font-semibold leading-6 text-green-800 hover:text-green-950 hover:underline"
                    >
                      {contact.detail}
                    </a>
                  ) : (
                    <p className="mt-3 break-words text-sm font-semibold leading-6 text-slate-700">
                      {contact.detail}
                    </p>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ================= FOOTER STRIP ================= */}
      <footer className="bg-green-950 px-5 py-6 text-center text-sm text-green-50/75">
        <p>
          © {new Date().getFullYear()} Bandarban University. All rights
          reserved.
        </p>
        <p className="mt-1 text-xs text-green-50/50">
          For official fee confirmation, please contact the university.
        </p>
      </footer>
    </div>
  );
}